/**
 * LLM provider, model, and agent catalog (frontend mirror).
 *
 * Single source of truth for the Settings → LLM page. Keep this in
 * lockstep with `backend/app/services/llm_catalog.py` — when you add or
 * retire an entry on one side, do the other in the same change.
 */

export type ProviderId = 'openai' | 'anthropic' | 'google' | 'deepseek';
export type AgentId = 'claude_code' | 'codex' | 'opencode';
export type ThinkingLevel = 'off' | 'low' | 'medium' | 'high';

export interface ModelSpec {
  id: string;
  label: string;
  supports_temperature: boolean;
  supports_max_tokens: boolean;
  supports_thinking: boolean;
}

export interface ProviderSpec {
  id: ProviderId;
  label: string;
  /** Display-only — used in tooltips so users know which env var their key sets. */
  api_key_env: string;
  models: ModelSpec[];
}

export interface AgentSpec {
  id: AgentId;
  label: string;
  binary: string;
  supports_thinking: boolean;
  /** True only for Claude Code — others require a pasted JSON credentials file. */
  oauth_env_native: boolean;
}

export const PROVIDERS: ProviderSpec[] = [
  {
    id: 'openai',
    label: 'OpenAI',
    api_key_env: 'OPENAI_API_KEY',
    models: [
      // GPT-6 Astra — OpenAI's most capable model, GA 2026-09
      // (developers.openai.com/api/docs/models/gpt-6-astra). Reasoning model:
      // 1.05M context, 128k max output, reasoning effort
      // low|medium|high|xhigh|max, no sampling params. $10/$50 MTok (2x input
      // / 1.5x output above 272k input tokens). Knowledge cutoff 2026-04-30.
      // Single model — OpenAI published no mini/nano GPT-6 sibling.
      { id: 'gpt-6-astra', label: 'GPT-6 Astra (latest)', supports_temperature: false, supports_max_tokens: true, supports_thinking: true },
      // GPT-6.1 Sol — successor to GPT-5.6 Sol: near-Astra quality at $2/$10
      // MTok (2x input / 1.5x output above 272k input tokens). 1.05M context
      // (922k max input), 128k max output, reasoning effort
      // low|medium|high|xhigh|max. Codex's recommended model.
      { id: 'gpt-6.1-sol', label: 'GPT-6.1 Sol', supports_temperature: false, supports_max_tokens: true, supports_thinking: true },
      // GPT-6 Luna — successor to GPT-5.6 Luna, OpenAI's most efficient model:
      // $0.10/$0.50 MTok, same 1.05M/128k limits, reasoning effort
      // none|low|medium|high|xhigh|max. GPT-6 has no Terra; GPT-5.6 Terra stays
      // the balanced tier.
      { id: 'gpt-6-luna', label: 'GPT-6 Luna', supports_temperature: false, supports_max_tokens: true, supports_thinking: true },
      // GPT-5.6 family — GA 2026-07-09 across ChatGPT, Codex and the API
      // (developers.openai.com/api/docs/models). Reasoning models: 1.05M
      // context, 128k max output, reasoning effort none|low|medium|high|xhigh,
      // no sampling params. Sol = frontier; Terra = intelligence/cost balance;
      // Luna = cost-optimized. Knowledge cutoff 2026-02-16.
      { id: 'gpt-5.6-sol', label: 'GPT-5.6 Sol', supports_temperature: false, supports_max_tokens: true, supports_thinking: true },
      { id: 'gpt-5.6-terra', label: 'GPT-5.6 Terra', supports_temperature: false, supports_max_tokens: true, supports_thinking: true },
      { id: 'gpt-5.6-luna', label: 'GPT-5.6 Luna', supports_temperature: false, supports_max_tokens: true, supports_thinking: true },
      // GPT-5.5 (verified via developers.openai.com/api/docs/models/gpt-5.5).
      // 1.05M context, 128k max output, supports streaming / function
      // calling / structured outputs, reasoning effort
      // none|low|medium|high|xhigh. Snapshot: gpt-5.5-2026-04-23.
      // Pro = smarter/more-precise variant.
      { id: 'gpt-5.5', label: 'GPT-5.5', supports_temperature: false, supports_max_tokens: true, supports_thinking: true },
      { id: 'gpt-5.5-pro', label: 'GPT-5.5 Pro', supports_temperature: false, supports_max_tokens: true, supports_thinking: true },
      // GPT-5.4 family (verified via developers.openai.com). 5.4 has the
      // same 1.05M context as 5.5 at roughly half the price; 5.4-mini
      // drops to 400k context for one-fifth the input cost.
      // Snapshots: gpt-5.4-2026-03-05 / gpt-5.4-mini-2026-03-17.
      { id: 'gpt-5.4', label: 'GPT-5.4', supports_temperature: false, supports_max_tokens: true, supports_thinking: true },
      { id: 'gpt-5.4-mini', label: 'GPT-5.4 mini', supports_temperature: false, supports_max_tokens: true, supports_thinking: true },
      { id: 'gpt-5', label: 'GPT-5', supports_temperature: false, supports_max_tokens: true, supports_thinking: true },
      { id: 'gpt-5-mini', label: 'GPT-5 mini', supports_temperature: false, supports_max_tokens: true, supports_thinking: true },
      { id: 'gpt-4.1', label: 'GPT-4.1', supports_temperature: true, supports_max_tokens: true, supports_thinking: false },
      { id: 'gpt-4.1-mini', label: 'GPT-4.1 mini', supports_temperature: true, supports_max_tokens: true, supports_thinking: false },
      { id: 'gpt-4o', label: 'GPT-4o', supports_temperature: true, supports_max_tokens: true, supports_thinking: false },
      { id: 'o3', label: 'o3', supports_temperature: false, supports_max_tokens: true, supports_thinking: true },
      { id: 'o4-mini', label: 'o4-mini', supports_temperature: false, supports_max_tokens: true, supports_thinking: true },
    ],
  },
  {
    id: 'anthropic',
    label: 'Anthropic',
    api_key_env: 'ANTHROPIC_API_KEY',
    models: [
      // Claude 5 family + Opus 4.7/4.8 share the adaptive-thinking API
      // surface: budget_tokens AND sampling params (temperature/top_p/
      // top_k) are removed — sending either is an HTTP 400 — so
      // supports_temperature is false for all of them.
      // Fable 5.1 — Anthropic's most capable model, successor to Fable 5 in
      // the same tier at the same price. 1M context, 128k max output,
      // thinking always on (adaptive), $10/$50 MTok. Forced tool_choice
      // ("any"/"tool") is rejected with a 400.
      { id: 'claude-fable-5-1', label: 'Claude Fable 5.1', supports_temperature: false, supports_max_tokens: true, supports_thinking: true },
      // Fable 5 — still served, same surface and price as 5.1.
      { id: 'claude-fable-5', label: 'Claude Fable 5', supports_temperature: false, supports_max_tokens: true, supports_thinking: true },
      // Opus 5.5 — successor to Opus 5 (released 2026-09-22), $4/$20 MTok. 1M
      // context, 128k max output, adaptive thinking ALWAYS on (depth via
      // effort, default medium). Forced tool_choice is rejected with a 400.
      { id: 'claude-opus-5-5', label: 'Claude Opus 5.5', supports_temperature: false, supports_max_tokens: true, supports_thinking: true },
      // Opus 5 — legacy, still served. Same adaptive-thinking surface as the
      // rest of the family.
      { id: 'claude-opus-5', label: 'Claude Opus 5', supports_temperature: false, supports_max_tokens: true, supports_thinking: true },
      // Opus 4.8 was confirmed via Anthropic's "What's new in Claude
      // Opus 4.8" docs page: 1M context, 128k max output, adaptive
      // thinking, sampling params (temperature/top_p/top_k) NOT
      // supported. ID follows the same dateless format Opus 4.6/4.7
      // adopted.
      { id: 'claude-opus-4-8', label: 'Claude Opus 4.8', supports_temperature: false, supports_max_tokens: true, supports_thinking: true },
      { id: 'claude-opus-4-7', label: 'Claude Opus 4.7', supports_temperature: false, supports_max_tokens: true, supports_thinking: true },
      // Sonnet 5.5 — successor to Sonnet 5 (released 2026-09-28), $2/$10 MTok.
      // 1M context, 128k max output, adaptive thinking on by default (lowest
      // setting is {type: 'between_tools'} — 'disabled' is a 400). Non-default
      // sampling params and forced tool_choice are rejected with a 400.
      { id: 'claude-sonnet-5-5', label: 'Claude Sonnet 5.5', supports_temperature: false, supports_max_tokens: true, supports_thinking: true },
      // Sonnet 5 — legacy, still served at $2/$10 MTok (the launch price became
      // standard; the planned rise to $3/$15 was cancelled).
      { id: 'claude-sonnet-5', label: 'Claude Sonnet 5', supports_temperature: false, supports_max_tokens: true, supports_thinking: true },
      { id: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6', supports_temperature: true, supports_max_tokens: true, supports_thinking: true },
      // Haiku 5.5 — successor to Haiku 4.5, $0.10/$0.50 MTok up to 100k-token
      // prompts ($0.50/$2.50 beyond). 1M context, 128k max output. First Haiku
      // with effort (low|medium|high|xhigh|max, default medium), replacing
      // budget_tokens; adaptive thinking on by default. budget_tokens,
      // non-default sampling params and prefill are rejected with a 400.
      { id: 'claude-haiku-5-5', label: 'Claude Haiku 5.5', supports_temperature: false, supports_max_tokens: true, supports_thinking: true },
      // Haiku 4.5 — previous generation: thinking via budget_tokens only, no effort.
      { id: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5', supports_temperature: true, supports_max_tokens: true, supports_thinking: true },
    ],
  },
  {
    id: 'google',
    label: 'Google Gemini',
    api_key_env: 'GEMINI_API_KEY',
    models: [
      // Gemini 3 first: Google 404s the 2.5 ids for API projects created after
      // their cut-off ("no longer available to new users"), so a new key must
      // land on a 3.x model. The 2.5 ids stay for projects already using them.
      // 3.8 Flash — 1,048,576-token input / 65,536-token output, on Google's
      // introductory $0.75/$3.75 MTok through 2026-12-31 ($1.50/$7.50 after).
      { id: 'gemini-3.8-flash', label: 'Gemini 3.8 Flash (latest)', supports_temperature: true, supports_max_tokens: true, supports_thinking: true },
      { id: 'gemini-3.7-flash', label: 'Gemini 3.7 Flash', supports_temperature: true, supports_max_tokens: true, supports_thinking: true },
      { id: 'gemini-3.6-flash', label: 'Gemini 3.6 Flash', supports_temperature: true, supports_max_tokens: true, supports_thinking: true },
      { id: 'gemini-3.5-flash', label: 'Gemini 3.5 Flash', supports_temperature: true, supports_max_tokens: true, supports_thinking: true },
      { id: 'gemini-3.5-flash-lite', label: 'Gemini 3.5 Flash Lite', supports_temperature: true, supports_max_tokens: true, supports_thinking: false },
      { id: 'gemini-3.1-flash-lite', label: 'Gemini 3.1 Flash Lite', supports_temperature: true, supports_max_tokens: true, supports_thinking: false },
      { id: 'gemini-3.1-pro-preview', label: 'Gemini 3.1 Pro (Preview)', supports_temperature: true, supports_max_tokens: true, supports_thinking: true },
      { id: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro (legacy)', supports_temperature: true, supports_max_tokens: true, supports_thinking: true },
      { id: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash (legacy)', supports_temperature: true, supports_max_tokens: true, supports_thinking: true },
      { id: 'gemini-2.5-flash-lite', label: 'Gemini 2.5 Flash Lite (legacy)', supports_temperature: true, supports_max_tokens: true, supports_thinking: false },
    ],
  },
  {
    id: 'deepseek',
    label: 'DeepSeek',
    api_key_env: 'DEEPSEEK_API_KEY',
    models: [
      { id: 'deepseek-chat', label: 'DeepSeek Chat (V3)', supports_temperature: true, supports_max_tokens: true, supports_thinking: false },
      { id: 'deepseek-reasoner', label: 'DeepSeek Reasoner (R1)', supports_temperature: false, supports_max_tokens: true, supports_thinking: true },
    ],
  },
];

export const AGENTS: AgentSpec[] = [
  { id: 'claude_code', label: 'Claude Code', binary: 'claude', supports_thinking: true, oauth_env_native: true },
  { id: 'codex', label: 'OpenAI Codex', binary: 'codex', supports_thinking: true, oauth_env_native: false },
  // OpenCode — provider-agnostic. Its models are whatever the providers
  // configured in OpenCode on the server expose, read live from the CLI
  // (see `loadOpencodeModels` / `useAgentModels`), never a static list.
  { id: 'opencode', label: 'OpenCode', binary: 'opencode', supports_thinking: true, oauth_env_native: false },
];

/** Tasks eligible for per-task config overrides — mirrors backend TASK_TYPES
 * (settings_service.TASK_TYPES). Keep the two in lockstep. */
export const TASK_TYPES = [
  'frs',
  'enhance_frs',
  'enhance_spec',
  'app_json',
  'model',
  'business_logic',
  'code_adoption',
  'source_explore',
  'page',
  'visual',
  'schema',
  'dashboard',
  'report',
  'project_modify',
  'assistant',
  'review',
  'ops',
  'test_data',
  'integration',
  'general_process',
  'approval_workflow',
  'business_area',
] as const;

export type TaskType = (typeof TASK_TYPES)[number];

export const TASK_LABELS: Record<TaskType, string> = {
  frs: 'FRS generation',
  enhance_frs: 'Enhance FRS',
  enhance_spec: 'Enhance spec',
  app_json: 'App JSON',
  model: 'Model generation',
  business_logic: 'Business logic (code)',
  code_adoption: 'Code adoption (analyze source)',
  source_explore: 'Data-source exploration',
  page: 'Page generation',
  visual: 'Visual design',
  schema: 'Schema generation',
  dashboard: 'Dashboard generation',
  report: 'Report generation',
  project_modify: 'Project modify (Ask AI)',
  assistant: 'Runtime Assistant',
  review: 'Review / validate (read-only)',
  ops: 'DB / deploy / access ops',
  test_data: 'Unit test & sample data',
  integration: 'Integration design',
  general_process: 'General Process (G)',
  approval_workflow: 'Approval workflow (W)',
  business_area: 'Business Area (A)',
};

export function getProvider(id: ProviderId): ProviderSpec | undefined {
  return PROVIDERS.find((p) => p.id === id);
}

export function getModel(providerId: ProviderId, modelId: string): ModelSpec | undefined {
  return getProvider(providerId)?.models.find((m) => m.id === modelId);
}

export function getAgent(id: AgentId): AgentSpec | undefined {
  return AGENTS.find((a) => a.id === id);
}

/**
 * Each agent CLI consumes its parent provider's models — Claude Code talks
 * to Anthropic, Codex to OpenAI. The config editor
 * reuses the provider's model list so the user gets the same dropdown for
 * both API and Agent modes (no separate model registry to keep in sync).
 */
export const AGENT_PROVIDER: Partial<Record<AgentId, ProviderId>> = {
  claude_code: 'anthropic',
  codex: 'openai',
  // opencode: none — its model ids name their provider (`openai/gpt-5`).
};

/** The provider an OpenCode model id names (`openai/gpt-5` → `openai`), when
 * it is one Twasta stores an API key for — what an api_key-auth OpenCode
 * configuration borrows its key from. */
export function opencodeModelProvider(model?: string): ProviderId | undefined {
  const p = (model || '').split('/')[0]?.trim().toLowerCase();
  return (model || '').includes('/') && PROVIDERS.some((x) => x.id === p)
    ? (p as ProviderId)
    : undefined;
}

/** One model OpenCode can run on this server (GET /users/settings/llm/opencode/models). */
export interface OpencodeModel {
  id: string;
  provider: string;
  name: string;
  label: string;
  free: boolean;
  reasoning: boolean;
  variants: string[];
}

let opencodeModelCache: ModelSpec[] = [];

/** Fill the OpenCode model cache `modelsForAgent('opencode')` reads. */
export function setOpencodeModels(models: OpencodeModel[]): ModelSpec[] {
  opencodeModelCache = models.map((m) => ({
    id: m.id,
    label: m.label,
    supports_temperature: false,
    supports_max_tokens: false,
    supports_thinking: m.reasoning,
  }));
  return opencodeModelCache;
}

/**
 * Per-agent model overrides — for agents whose CLI accepts a narrower set
 * of model IDs than its parent provider's full lineup.
 *
 * Codex CLI (`codex exec --model …`) accepts two distinct families:
 *
 *  1. **Standard OpenAI models** (`gpt-5`, `gpt-5-mini`, `o4-mini`, `o3`).
 *     Available via the public Responses API (`api.openai.com/v1/responses`).
 *     Work with BOTH subscription and api_key auth modes.
 *
 *  2. **Codex-tuned models** (`gpt-5-codex`, `gpt-5-codex-mini`). Available
 *     ONLY via the ChatGPT subscription endpoint
 *     (`chatgpt.com/backend-api/codex/`). With api_key auth the public
 *     Responses API rejects them with ``model_not_found``.
 *
 * The list is ordered: api-compatible (works in both auth modes) first,
 * subscription-only last with an explicit label so users can see why
 * picking one of them with an api key fails. We don't hide them entirely
 * because they're still the right pick for subscription-mode users.
 *
 * If an agent isn't listed here, `modelsForAgent` falls back to its
 * provider's full model list (current behaviour for `claude_code`).
 */
export const AGENT_MODELS: Partial<Record<AgentId, ModelSpec[]>> = {
  codex: [
    // GPT-6 Astra — configurable in Codex CLI from v0.153.1
    // (`codex exec --model gpt-6-astra`) and served by the public Responses
    // API, so it works under both auth modes.
    { id: 'gpt-6-astra', label: 'GPT-6 Astra (latest)',
      supports_temperature: false, supports_max_tokens: true, supports_thinking: true },
    // GPT-6.1 Sol — Codex's recommended model (near-Astra at a fraction of the
    // cost); GPT-6 Luna — the efficient one. Both work under API key AND
    // ChatGPT sign-in.
    { id: 'gpt-6.1-sol', label: 'GPT-6.1 Sol (recommended)',
      supports_temperature: false, supports_max_tokens: true, supports_thinking: true },
    { id: 'gpt-6-luna', label: 'GPT-6 Luna',
      supports_temperature: false, supports_max_tokens: true, supports_thinking: true },
    // GPT-5.6 family — GA 2026-07-09 in Codex + the public Responses API,
    // so usable under both subscription and api_key auth.
    { id: 'gpt-5.6-sol', label: 'GPT-5.6 Sol',
      supports_temperature: false, supports_max_tokens: true, supports_thinking: true },
    { id: 'gpt-5.6-terra', label: 'GPT-5.6 Terra',
      supports_temperature: false, supports_max_tokens: true, supports_thinking: true },
    { id: 'gpt-5.6-luna', label: 'GPT-5.6 Luna',
      supports_temperature: false, supports_max_tokens: true, supports_thinking: true },
    // GPT-5.5 — leaves ChatGPT-sign-in Codex on 2026-10-14 (API-key auth keeps
    // it after that date).
    { id: 'gpt-5.5', label: 'GPT-5.5',
      supports_temperature: false, supports_max_tokens: true, supports_thinking: true },
    // GPT-5.4 family — verified via developers.openai.com. Available
    // on the public Responses API so works for codex + api_key auth.
    { id: 'gpt-5.4', label: 'GPT-5.4',
      supports_temperature: false, supports_max_tokens: true, supports_thinking: true },
    { id: 'gpt-5.4-mini', label: 'GPT-5.4 mini',
      supports_temperature: false, supports_max_tokens: true, supports_thinking: true },
    // Universally available (api_key + subscription).
    { id: 'gpt-5', label: 'GPT-5',
      supports_temperature: false, supports_max_tokens: true, supports_thinking: true },
    { id: 'gpt-5-mini', label: 'GPT-5 mini',
      supports_temperature: false, supports_max_tokens: true, supports_thinking: true },
    { id: 'o4-mini', label: 'o4-mini',
      supports_temperature: false, supports_max_tokens: true, supports_thinking: true },
    { id: 'o3', label: 'o3',
      supports_temperature: false, supports_max_tokens: true, supports_thinking: true },
    // Subscription-only. Will fail with `model_not_found` if you're on
    // api_key auth — labels call that out so the picker is self-documenting.
    { id: 'gpt-5-codex', label: 'GPT-5 Codex (ChatGPT subscription only)',
      supports_temperature: false, supports_max_tokens: true, supports_thinking: true },
    { id: 'gpt-5-codex-mini', label: 'GPT-5 Codex mini (ChatGPT subscription only)',
      supports_temperature: false, supports_max_tokens: true, supports_thinking: true },
  ],
};

export function modelsForAgent(agent: AgentId): ModelSpec[] {
  if (agent === 'opencode') return opencodeModelCache;
  const override = AGENT_MODELS[agent];
  if (override) return override;
  const provider = AGENT_PROVIDER[agent];
  return provider ? getProvider(provider)?.models || [] : [];
}
